import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GIFT_ENTRY_LABELS, GIFT_SALE_METHOD_LABELS, formatCents, type GiftVoucherDetail } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { dayDe, giftBadge } from './GiftVouchersPage';
import './vouchers.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Das hat nicht geklappt.');

/** Ein Gutschein mit Verlauf aller Buchungen. Den Code gibt es hier nicht mehr, nur die letzten vier Zeichen zum Wiedererkennen. */
export function GiftVoucherPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const write = can('vouchers.write');
  const [error, setError] = useState<string | null>(null);
  const [expiresOn, setExpiresOn] = useState('');

  const query = useQuery({ queryKey: ['gift-voucher', id], queryFn: async () => (await api<{ data: GiftVoucherDetail }>('GET', `/gift-vouchers/${id}`)).data });
  const done = (d: GiftVoucherDetail) => {
    client.setQueryData(['gift-voucher', id], d);
    void client.invalidateQueries({ queryKey: ['gift-vouchers'] });
    setError(null);
  };
  const voidIt = useMutation({
    mutationFn: async (reason: string) => (await api<{ data: GiftVoucherDetail }>('POST', `/gift-vouchers/${id}/void`, { reason })).data,
    onSuccess: done,
    onError: (e) => setError(errorText(e)),
  });
  const extend = useMutation({
    mutationFn: async () => (await api<{ data: GiftVoucherDetail }>('PATCH', `/gift-vouchers/${id}`, { expiresOn })).data,
    onSuccess: (d) => { done(d); setExpiresOn(''); },
    onError: (e) => setError(errorText(e)),
  });

  if (query.isLoading) return <p className="muted">Wird geladen …</p>;
  if (query.isError || !query.data) return <div className="card narrow"><h1>Gutschein nicht gefunden</h1><Link to="/gutscheine">Zur Übersicht</Link></div>;
  const v = query.data;
  const open = v.status === 'active' || v.status === 'expired';

  return (
    <>
      <p><Link to="/gutscheine" className="back-link">← Gutscheine</Link></p>
      <div className="page-head">
        <h1>{v.number}</h1>
        {giftBadge(v.status)}
        <span className="muted">Code endet auf …{v.codeHint}</span>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {v.status === 'void' && <div className="alert alert--warn" role="status">Gesperrt am {v.voidedAt ? stamp.format(new Date(v.voidedAt)) : ''}: {v.voidReason}</div>}

      <div className="payroll-tiles">
        <div className="card payroll-tile"><span className="muted small">Guthaben</span><strong>{formatCents(v.balanceCents)}</strong><span className="muted small">von {formatCents(v.initialCents)}</span></div>
        <div className="card payroll-tile"><span className="muted small">Gültig bis</span><strong>{dayDe(v.expiresOn)}</strong></div>
        <div className="card payroll-tile"><span className="muted small">Empfänger</span><strong className="gift-text">{v.recipientName ?? '–'}</strong>{v.message && <span className="muted small">„{v.message}“</span>}</div>
        <div className="card payroll-tile"><span className="muted small">Käufer</span><strong className="gift-text">{v.buyerName ?? '–'}</strong>{v.buyerContactId && <Link className="small" to={`/kontakte/${v.buyerContactId}`}>Zum Kontakt</Link>}</div>
      </div>
      <p className="muted">{v.origin === 'sold' ? `Verkauft, bezahlt per ${v.saleMethod ? GIFT_SALE_METHOD_LABELS[v.saleMethod] : ''} am ${dayDe(v.salePaidOn)}. Verbindlichkeit auf Konto ${v.saleAccount}.` : 'Verschenkt: Es gab keinen Zahlungseingang, gebucht wird nichts, erst die Einlösung mindert die Rechnung.'}</p>
      {v.note && <p className="muted">Notiz: {v.note}</p>}

      <div className="card table-card">
        <h2 className="gift-ledger-title">Verlauf</h2>
        <table className="table table--cards">
          <thead><tr><th>Wann</th><th>Vorgang</th><th className="num">Betrag</th><th className="num">Guthaben danach</th><th>Bezug</th><th>Von</th></tr></thead>
          <tbody>
            {v.entries.map((e) => (
              <tr key={e.id}>
                <td data-label="Wann" className="nowrap">{stamp.format(new Date(e.at))}</td>
                <td data-label="Vorgang">{GIFT_ENTRY_LABELS[e.kind]}</td>
                <td data-label="Betrag" className="num">{e.amountCents > 0 ? '+' : ''}{formatCents(e.amountCents)}</td>
                <td data-label="Guthaben danach" className="num">{formatCents(e.balanceAfterCents)}</td>
                <td data-label="Bezug">{e.invoiceId ? <Link to={`/rechnungen/${e.invoiceId}`}>Rechnung {e.invoiceNumber}</Link> : (e.reference ?? '–')}</td>
                <td data-label="Von">{e.byName ?? '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {write && open && (
        <div className="card">
          <h2>Verwalten</h2>
          <div className="form-grid form-grid--aligned">
            <label className="field"><span>Gültigkeit verlängern bis</span><input className="input" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} /></label>
            <div className="form-actions"><button className="btn btn--secondary" type="button" disabled={!expiresOn || extend.isPending} onClick={() => extend.mutate()}>Verlängern</button></div>
          </div>
          <div className="payroll-actions">
            <button
              className="btn btn--danger"
              type="button"
              disabled={voidIt.isPending}
              onClick={() => {
                const reason = window.prompt(`Gutschein ${v.number} sperren? Das Guthaben von ${formatCents(v.balanceCents)} verfällt. Grund (mindestens 5 Zeichen):`);
                if (reason) voidIt.mutate(reason);
              }}
            >
              Gutschein sperren
            </button>
          </div>
          <p className="muted small">Ein verlorener Gutschein lässt sich nicht wiederherstellen, denn Wirkia kennt den Code nicht. Sperren Sie ihn und stellen Sie einen neuen mit dem Restbetrag aus.</p>
        </div>
      )}
    </>
  );
}
